import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';
import {
  IProfile,
  IProfileEntity,
  IProfileLocation,
  LocationType,
} from '@boma/website/shared/entities';
import { EntityType } from 'libs/shared/entities/src/lib/enums/entity-type';
import { BehaviorSubject, lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  data$ = new BehaviorSubject<{
    profile: IProfile | null;
    selectedEntity: IProfileEntity | null;
    selectedLocation: IProfileLocation | null;
    userIsOperatingInDriverContext: boolean;
    permissions: string[];
  } | null>(null);

  private readonly loadingProfileSubject = new BehaviorSubject(false);
  loadingProfile$ = this.loadingProfileSubject.asObservable();

  private base: string;
  private version: string;
  private profileSelectedEntityIdLocalStorageKey = 'BM_PRFL_SLC_ENT';
  private profileSelectedLocationIdLocalStorageKey = 'BM_PRFL_SLC_LCN';

  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private httpClient: HttpClient
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  private _profile: IProfile | null;

  public get profile() {
    return this._profile;
  }

  private _locations: IProfileLocation[] | null;

  public get locations() {
    return this._locations;
  }

  private _selectedEntityId: string | null;

  public get selectedEntityId() {
    if (this._selectedEntityId) {
      return this._selectedEntityId;
    }

    if (typeof localStorage !== 'undefined') {
      const storedSelectedEntityId = localStorage.getItem(
        this.profileSelectedEntityIdLocalStorageKey
      );
      if (storedSelectedEntityId) {
        this._selectedEntityId = storedSelectedEntityId;
        return storedSelectedEntityId;
      }
    }

    return null;
  }

  private _selectedLocationId: string | null;

  public get selectedLocationId() {
    if (this._selectedLocationId) {
      return this._selectedLocationId;
    }

    if (typeof localStorage !== 'undefined') {
      const storedSelectedLocationId = localStorage.getItem(
        this.profileSelectedLocationIdLocalStorageKey
      );
      if (storedSelectedLocationId) {
        this._selectedLocationId = storedSelectedLocationId;
        return storedSelectedLocationId;
      }
    }

    return null;
  }

  public get selectedLocation(): IProfileLocation | null {
    if (this.locations == null || this.locations.length <= 0) {
      return null;
    }

    if (this.locations.filter((l) => l.id === this.selectedLocationId).length) {
      return this.locations.filter((l) => l.id === this.selectedLocationId)[0];
    }

    return null;
  }

  public get selectedEntity(): IProfileEntity | null {
    if (this.profile?.entities == null || this.profile.entities.length <= 0) {
      return null;
    }

    if (
      this.profile.entities.filter((e) => e.id === this.selectedEntityId).length
    ) {
      return this.profile.entities.filter(
        (e) => e.id === this.selectedEntityId
      )[0];
    }

    return null;
  }

  get userIsOperatingInDriverContext(): boolean {
    if (!this.selectedEntity) {
      return false;
    }

    return (
      this.selectedEntity.type === EntityType.Supplier &&
      this.selectedLocation.type === LocationType.Vehicle
    );
  }

  /**
   * Loads, sets and returns the profile of the currently logged in user
   * @returns Profile of the user
   */
  async loadUserProfile(): Promise<IProfile> {
    this.loadingProfileSubject.next(true);
    this._profile = await lastValueFrom(this._getProfileFromApi()).catch(
      (reason) => {
        console.log('E1547:', reason); // Profile Stuff
        this.loadingProfileSubject.next(false);
        return null;
      }
    );

    // if we don't have a selected id or if we have a previously selected entity and it is no longer present in their profile
    // then we default to whatever is available
    if (
      !this.selectedEntityId ||
      (this.selectedEntityId &&
        this.profile.entities.filter((e) => e.id === this.selectedEntityId)
          .length <= 0)
    ) {
      this.clearSelectedEntityId();
      this.autoSelectEntity();
    }

    // if at this point we don't have a selection - just return - they will need to create a profile
    if (!this.selectedEntityId) {
      this._notifyListeners();
      return this.profile;
    }

    // get locations for the selected entity
    this._locations = await lastValueFrom(
      this._getLocationsForProfileFromApi(this.selectedEntityId)
    );
    if (
      !this.selectedLocationId ||
      (this.selectedLocationId &&
        this.locations.filter((l) => l.id === this.selectedLocationId).length <=
          0)
    ) {
      this.clearSelectedLocationId();
      if (this.profile.entities.length) {
        this.setSelectedLocationId(this.locations[0].id);
      }
    }

    // if at this point we don't have a location - something has gone very wrong and we need to panic
    if (!this.selectedLocationId) {
      console.log('E7894451');
    }

    this._notifyListeners();
    return this.profile;
  }

  private autoSelectEntity() {
    if (this.profile?.entities && this.profile.entities.length > 0) {
      const nonConsumerEntity = this.profile.entities.find(
        (e) => e.type !== EntityType.Consumer
      );

      if (nonConsumerEntity) {
        this.setSelectedEntityId(nonConsumerEntity.id);
      }
    }
  }

  getLocationsForEntity(entityId: string) {
    return this._getLocationsForProfileFromApi(entityId);
  }

  async switchContext(entityId: string, locationId?: string) {
    this.clearSelectedEntityId();
    this.clearSelectedLocationId();
    this.setSelectedEntityId(entityId);
    this.setSelectedLocationId(locationId);

    // this is kind of a hack - but a neat one
    await this.loadUserProfile();
  }

  private clearSelectedLocationId() {
    this._selectedLocationId = null;
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(this.profileSelectedLocationIdLocalStorageKey);
    }
  }

  private setSelectedLocationId(locationId: string) {
    this._selectedLocationId = locationId;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(
        this.profileSelectedLocationIdLocalStorageKey,
        locationId
      );
    }
  }

  private clearSelectedEntityId() {
    this._selectedEntityId = null;
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(this.profileSelectedEntityIdLocalStorageKey);
    }
  }

  private setSelectedEntityId(entityId: string) {
    this._selectedEntityId = entityId;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(
        this.profileSelectedEntityIdLocalStorageKey,
        entityId
      );
    }
  }

  hasPermissions(
    requiredPermissions: string[],
    requirement: 'all' | 'some' = 'some'
  ): boolean {
    if (!this.selectedEntity || !this.selectedEntity?.permissions) {
      console.log('E7895'); // No Entity Was Selected.
      return false;
    }
    const hasAllPermissions = requiredPermissions.every((permission) =>
      this.selectedEntity.permissions.includes(permission)
    );
    const hasSomePermissions = requiredPermissions.some((permission) =>
      this.selectedEntity.permissions.includes(permission)
    );

    if (requirement === 'all') {
      return hasAllPermissions;
    } else {
      return hasSomePermissions;
    }
  }

  private _getData(): {
    profile: IProfile | null;
    selectedEntity: IProfileEntity | null;
    selectedLocation: IProfileLocation | null;
    userIsOperatingInDriverContext: boolean;
    permissions: string[];
  } {
    return {
      profile: this.profile,
      selectedEntity: this.selectedEntity,
      selectedLocation: this.selectedLocation,
      userIsOperatingInDriverContext: this.userIsOperatingInDriverContext,
      permissions: this.selectedEntity?.permissions ?? [],
    };
  }

  private _notifyListeners() {
    this.data$.next(this._getData());
  }

  private _getProfileFromApi() {
    return this.httpClient.get<IProfile>(
      `${this.base}api/v${this.version}/Profiles/me`
    );
  }

  private _getLocationsForProfileFromApi(entityId: string) {
    return this.httpClient.get<IProfileLocation[]>(
      `${this.base}api/v${this.version}/Profiles/me/${entityId}/Locations`
    );
  }
}
